import { useMutation } from "react-query";
import { AxiosError } from "axios";
import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
} from "@simplewebauthn/browser";
import HttpService from "../../services/http";

/**
 * Passwordless (magic link + secret word) and passkey (WebAuthn) login.
 *
 * Owner accounts resolve their entity server-side from the email, so the caller only ever supplies
 * an email. A successful ceremony returns the same access token the password login returns.
 */

/**
 * UMD's messages are written for developers ("This user has no passkey registered."). Each refusal
 * a person can actually hit is restated here as something they can act on.
 */
const PASSKEY_ERROR_KEYS: Record<string, string> = {
  RSUSKO041: "passkey.errorNoneRegistered",
  RSUSKO026: "passkey.errorNoAccount",
  RSUSKO015: "passkey.errorNoAccount",
  RSUSKO043: "passkey.errorTooManyAttempts",
  RSUSKO040: "passkey.errorUnknownCredential",
  RSUSKO039: "passkey.errorNotVerified",
  RSUSKO042: "passkey.errorAlreadyRegistered",
  RSUSKO037: "passkey.errorNoCeremony",
  RSUSKO038: "passkey.errorCeremonyExpired",
  RSUSKO014: "passkey.errorAccountNotActive",
  RSUSKO029: "passkey.errorBanned",
};

/**
 * The browser refuses before the server ever hears about it — most often because this device
 * already holds a key for the account, which `excludeCredentials` tells the authenticator to
 * reject. @simplewebauthn/browser wraps those as a WebAuthnError carrying a `code`.
 */
const PASSKEY_BROWSER_ERROR_KEYS: Record<string, string> = {
  ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED: "passkey.errorAlreadyRegistered",
  ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT:
    "passkey.errorNoDiscoverableSupport",
  ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT:
    "passkey.errorNoUserVerification",
  ERROR_INVALID_DOMAIN: "passkey.errorBadOrigin",
  ERROR_INVALID_RP_ID: "passkey.errorBadOrigin",
};

/** Outcomes that are not failures: the user simply already has what they asked for. */
const PASSKEY_BENIGN_KEYS = new Set(["passkey.errorAlreadyRegistered"]);

/**
 * @returns the translation key to show, or null when the user dismissed the browser prompt —
 *   a choice, not a failure, and it deserves no message at all.
 */
export const passkeyErrorKey = (error: any): string | null => {
  if (error?.name === "NotAllowedError" || error?.name === "AbortError") {
    return null;
  }

  const browserKey = error?.code && PASSKEY_BROWSER_ERROR_KEYS[error.code];
  if (browserKey) {
    return browserKey;
  }

  const code = error?.response?.data?.code;
  return (code && PASSKEY_ERROR_KEYS[code]) || "passkey.error";
};

/**
 * Show the right message with the right tone. Kept here so the three places that run a ceremony
 * cannot drift apart on it.
 */
export const notifyPasskeyError = (
  error: any,
  t: (key: string) => unknown,
  notify: { error: (message: string) => void; info: (message: string) => void }
): void => {
  const key = passkeyErrorKey(error);
  if (!key) {
    return;
  }
  const message = t(key) as string;
  if (PASSKEY_BENIGN_KEYS.has(key)) {
    notify.info(message);
  } else {
    notify.error(message);
  }
};

export const isPasskeySupported = (): boolean => {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
};

/**
 * Tell the browser to stop offering a credential we no longer hold.
 *
 * Deleting a passkey server-side removes our half of the pair; the private key stays in the user's
 * password manager, and the browser keeps proposing it in the address field's autofill. The user
 * then picks a key that can only be refused. `signalUnknownCredential` is the WebAuthn call made
 * for exactly this, and it is the only way we can reach into their credential store.
 *
 * Best-effort: unsupported browsers simply ignore it, and a failure here must never surface.
 */
const disownCredential = async (
  rpId?: string,
  credentialId?: string
): Promise<void> => {
  const api = (window as any).PublicKeyCredential;
  if (
    !rpId ||
    !credentialId ||
    typeof api?.signalUnknownCredential !== "function"
  ) {
    return;
  }
  try {
    await api.signalUnknownCredential({ rpId, credentialId });
  } catch {
    // The browser declined to act on it. Nothing else we can do, and nothing the user needs told.
  }
};

/** @returns true when the server says it does not know the credential that was presented */
const isUnknownCredential = (error: any): boolean =>
  error?.response?.data?.code === "RSUSKO040";

/** WebAuthn binds a credential to the page's origin; the server checks it against an allow-list. */
const currentOrigin = (): string => window.location.origin;

export type PassLessRequestPayload = {
  user_email: string;
  secret_word: string;
  /**
   * Set when the caller is a team member, who supplies their entity on the login screen.
   *
   * Owner accounts are unique platform-wide, so their entity can be resolved from the address
   * alone. A team member's address is only unique WITHIN an entity — and may well also be the
   * address of an owner somewhere else — so resolving from the address would land on the wrong
   * account. Given an entity, the entity-scoped endpoints are used instead. They look at no role:
   * owner or any custom role, the path is the same.
   */
  entity_identification?: string;
};

export type PassLessPollPayload = {
  entity_identification: string;
  user_identification: string;
  secret_word: string;
};

export type PasskeyLoginPayload = {
  user_email: string;
  /** Same rationale as PassLessRequestPayload. */
  entity_identification?: string;
};

export const useRequestPassLessLink = () =>
  useMutation<any, AxiosError<any, any>, PassLessRequestPayload>(
    ({ entity_identification, ...payload }) =>
      entity_identification
        ? HttpService.post(
            `/auth/${entity_identification}/login/passless`,
            payload
          )
        : HttpService.post("/auth/owner/login/passless", payload)
  );

/**
 * One poll of a pending passwordless request. Answers 401 until the user confirms on the emailed
 * page, so the caller drives the retry rather than react-query.
 */
export const usePassLessStatus = () =>
  useMutation<any, AxiosError<any, any>, PassLessPollPayload>(
    ({ entity_identification, user_identification, secret_word }) =>
      HttpService.post(
        `/auth/${entity_identification}/${user_identification}/login/passless/status`,
        { secret_word }
      )
  );

export const usePassLessServerValidation = () =>
  useMutation<
    any,
    AxiosError<any, any>,
    {
      entity_identification: string;
      user_identification: string;
      token: string;
    }
  >(({ entity_identification, user_identification, token }) =>
    HttpService.post(
      `/auth/${entity_identification}/${user_identification}/login/passless/server/validation`,
      { token }
    )
  );

/**
 * The hosted login page of an entity: the confirmed request is exchanged for the login page token,
 * returned inside `login_form_callback` like the password and social logins of that page.
 */
export const useLoginPagePassLessValidation = () =>
  useMutation<
    any,
    AxiosError<any, any>,
    {
      entity_identification: string;
      user_identification: string;
      token: string;
      redirect?: string | null;
    }
  >(({ entity_identification, user_identification, token, redirect }) =>
    HttpService.post(
      `/auth/${entity_identification}/${user_identification}/login-page/passless`,
      { token },
      { params: redirect ? { redirect } : {} }
    )
  );

type PassLessConfirmationTarget = {
  entity_identification: string;
  user_identification: string;
  token: string;
};

export const usePassLessConfirmationContext = () =>
  useMutation<any, AxiosError<any, any>, PassLessConfirmationTarget>(
    ({ entity_identification, user_identification, token }) =>
      HttpService.get(
        `/auth/${entity_identification}/${user_identification}/login/passless/confirmation`,
        { params: { token } }
      )
  );

export const useConfirmPassLessSecretWord = () =>
  useMutation<
    any,
    AxiosError<any, any>,
    PassLessConfirmationTarget & { secret_word: string }
  >(({ entity_identification, user_identification, token, secret_word }) =>
    HttpService.post(
      `/auth/${entity_identification}/${user_identification}/login/passless/confirmation`,
      { secret_word },
      { params: { token } }
    )
  );

export const usePasskeyLogin = () =>
  useMutation<any, AxiosError<any, any>, PasskeyLoginPayload>(
    async ({ user_email, entity_identification }) => {
      const base = entity_identification
        ? `/auth/${entity_identification}/passkey/authentication`
        : "/auth/owner/passkey/authentication";

      const optionsResponse = await HttpService.post(`${base}/options`, {
        user_email,
        origin: currentOrigin(),
      });

      const options = optionsResponse?.data?.element?.options;
      const assertion = await startAuthentication({ optionsJSON: options });

      try {
        return await HttpService.post(base, {
          user_email,
          origin: currentOrigin(),
          credential: assertion,
        });
      } catch (error) {
        if (isUnknownCredential(error)) {
          await disownCredential(options?.rpId, assertion?.id);
        }
        throw error;
      }
    }
  );

/**
 * Conditional UI: the browser offers a passkey from the address field's own autofill, if it holds
 * one for this site. Nothing is asked of the server about who the user is — the assertion carries
 * the user handle — so this reveals nothing about which accounts exist.
 *
 * The promise stays pending until the user picks the passkey, and rejects when they type instead.
 * That rejection is normal and must stay silent.
 */
export const startPasskeyAutofill = async (): Promise<any | null> => {
  try {
    if (
      !browserSupportsWebAuthn() ||
      !(await browserSupportsWebAuthnAutofill())
    ) {
      return null;
    }
  } catch {
    return null;
  }

  let optionsResponse;
  try {
    optionsResponse = await HttpService.post(
      "/auth/passkey/discoverable/options",
      {
        origin: currentOrigin(),
      }
    );
  } catch {
    return null;
  }

  const options = optionsResponse?.data?.element;
  if (!options) {
    return null;
  }
  const assertion = await startAuthentication({
    optionsJSON: options,
    useBrowserAutofill: true,
  });

  try {
    return await HttpService.post("/auth/passkey/discoverable", {
      origin: currentOrigin(),
      credential: assertion,
    });
  } catch (error) {
    if (isUnknownCredential(error)) {
      await disownCredential(options?.rpId, assertion?.id);
    }
    throw error;
  }
};

export type PasskeyScope = {
  entity_identification: string;
  user_identification: string;
};

export const useListPasskeys = () =>
  useMutation<any, AxiosError<any, any>, PasskeyScope>(
    ({ entity_identification, user_identification }) =>
      HttpService.get(
        `/auth/${entity_identification}/${user_identification}/passkey`
      )
  );

export const useRegisterPasskey = () =>
  useMutation<any, AxiosError<any, any>, PasskeyScope & { label?: string }>(
    async ({ entity_identification, user_identification, label }) => {
      const optionsResponse = await HttpService.post(
        `/auth/${entity_identification}/${user_identification}/passkey/registration/options`,
        { origin: currentOrigin() }
      );

      const attestation = await startRegistration({
        optionsJSON: optionsResponse?.data?.element,
      });

      return HttpService.post(
        `/auth/${entity_identification}/${user_identification}/passkey/registration`,
        { origin: currentOrigin(), credential: attestation, label }
      );
    }
  );

/** Exposed so the passkey list can disown a key in the browser right after removing it here. */
export const forgetPasskeyInBrowser = disownCredential;

export const useDeletePasskey = () =>
  useMutation<
    any,
    AxiosError<any, any>,
    PasskeyScope & { credential_identification: string }
  >(
    ({
      entity_identification,
      user_identification,
      credential_identification,
    }) =>
      HttpService.delete(
        `/auth/${entity_identification}/${user_identification}/passkey/${encodeURIComponent(
          credential_identification
        )}`
      )
  );
